import { CardEventType, CardWidgetField, CardWidgetFieldsValidatorResult, ICardEvent } from './CardFormWL.types';

enum CardFieldValidation {
  NONE = 'none',
  VALID = 'valid',
  INVALID = 'invalid',
}

interface ICardWidgetState {
  status: CardFieldValidation;
  touched: boolean;
}

export default class CardWidgetFieldsValidator {
  private cardNumberEvents: Array<ICardEvent> = [];

  private readonly trackEvent?: (event: ICardEvent) => void;

  cardWidgetFields: Map<string, ICardWidgetState> = new Map();

  constructor(trackEvent?: (event: ICardEvent) => void) {
    this.trackEvent = trackEvent;
  }

  /**
   * Initialize fresh map with new card widget states
   */
  init(): void {
    this.cardWidgetFields = new Map<string, ICardWidgetState>([
      [CardWidgetField.HOLDER_NAME, this.getInitValue()],
      [CardWidgetField.CARD_NUMBER, this.getInitValue()],
      [CardWidgetField.EXPIRATION, this.getInitValue()],
      [CardWidgetField.CVC, this.getInitValue()],
    ]);
    this.cardNumberEvents = [];
  }

  isAllFieldsValid(): boolean {
    return (
      Array.from(this.cardWidgetFields.values()).filter(event => event.status === CardFieldValidation.VALID).length ===
      4
    );
  }

  /**
   * For cardEvent with {@link CardEventType.BLUR} map record is updated with touched value. Additionally
   * if field name is {@link CardWidgetField.CARD_NUMBER} validation status is reset.
   * @param cardEvent
   * @returns boolean - true if there is any card number validation event in progress.
   */
  isBlockedByCardNumberValidation(cardEvent: ICardEvent): boolean {
    let isCardNumberEventInProgress = false;
    if (cardEvent.event === CardEventType.BLUR) {
      this.setFieldTouched(cardEvent.id);
      if (cardEvent.id === CardWidgetField.CARD_NUMBER) {
        isCardNumberEventInProgress = this.cardNumberEvents.findIndex(x => x.event === CardEventType.VALIDATED) > -1;
        if (!isCardNumberEventInProgress) {
          this.setFieldValidated(cardEvent.id, undefined);
          this.trackEvent?.(cardEvent);
        }
      }
    }
    return isCardNumberEventInProgress;
  }

  /**
   * Updates field validation based on {@link ICardEvent}
   * and afterwards checks validation of whole widget
   * @param cardEvent - {@link ICardEvent}
   * @returns - a validation result {@link CardWidgetFieldsValidatorResult}
   */
  getValidationResult(cardEvent: ICardEvent): CardWidgetFieldsValidatorResult {
    let result: CardWidgetFieldsValidatorResult = CardWidgetFieldsValidatorResult.NOT_READY;

    this.registerCardNumberEvent(cardEvent);
    if (cardEvent.event === CardEventType.VALIDATED) {
      this.setFieldValidated(cardEvent.id, cardEvent.isValid);
      this.trackEvent?.(cardEvent);
    }

    if (this.validateFieldsWithoutAsyncCalls()) {
      if (this.isFieldValidated(CardWidgetField.CARD_NUMBER)) {
        result = CardWidgetFieldsValidatorResult.SHOW_PAYMENT_BUTTON;
      } else if (this.isCardNumberFieldNotFinishedValidation()) {
        result = CardWidgetFieldsValidatorResult.SHOW_LOADER;
      }
    }
    this.removeCardNumberEvent(cardEvent);
    return result;
  }

  private registerCardNumberEvent(cardEvent: ICardEvent): void {
    if (this.isCardNumberValidationEvent(cardEvent)) {
      this.cardNumberEvents.push(cardEvent);
    }
  }

  private removeCardNumberEvent(cardEvent: ICardEvent) {
    if (this.isCardNumberValidationEvent(cardEvent)) {
      const removeCardNumberEventsCallback = () => this.cardNumberEvents.pop();
      setTimeout(removeCardNumberEventsCallback, 100);
    }
  }

  private validateFieldsWithoutAsyncCalls(): boolean | undefined {
    return (
      this.isFieldValidated(CardWidgetField.HOLDER_NAME) &&
      this.isFieldValidated(CardWidgetField.EXPIRATION) &&
      this.isFieldValidated(CardWidgetField.CVC)
    );
  }

  private isCardNumberFieldNotFinishedValidation(): boolean | undefined {
    return (
      this.isFieldTouched(CardWidgetField.CARD_NUMBER) &&
      this.isFieldHasStatus(CardWidgetField.CARD_NUMBER, CardFieldValidation.NONE)
    );
  }

  private isCardNumberValidationEvent(cardEvent: ICardEvent) {
    return cardEvent.event === CardEventType.VALIDATED && cardEvent.id === CardWidgetField.CARD_NUMBER;
  }

  private isFieldHasStatus(fieldName: string, status: CardFieldValidation): boolean | undefined {
    const cardWidgetField = this.cardWidgetFields.get(fieldName);
    return cardWidgetField ? cardWidgetField.status === status : undefined;
  }

  private isFieldValidated(fieldName: string): boolean | undefined {
    return this.isFieldHasStatus(fieldName, CardFieldValidation.VALID);
  }

  private isFieldTouched(fieldName: string): boolean | undefined {
    const cardWidgetField = this.cardWidgetFields.get(fieldName);
    return cardWidgetField ? cardWidgetField.touched : undefined;
  }

  private setFieldTouched(fieldName: string): void {
    const cardWidgetField = this.cardWidgetFields.get(fieldName);
    if (cardWidgetField) {
      cardWidgetField.touched = true;
    }
  }

  private setFieldValidated(fieldName: string, value: boolean | undefined): void {
    const cardWidgetField = this.cardWidgetFields.get(fieldName);
    if (cardWidgetField) {
      cardWidgetField.status = this.mapToCardFieldValidation(value);
    }
  }

  private mapToCardFieldValidation(value: boolean | undefined): CardFieldValidation {
    switch (value) {
      case true:
        return CardFieldValidation.VALID;
      case false:
        return CardFieldValidation.INVALID;
      default:
        return CardFieldValidation.NONE;
    }
  }

  private getInitValue(): ICardWidgetState {
    return { status: CardFieldValidation.NONE, touched: false } as ICardWidgetState;
  }
}
